import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { Button, TextField, Popover, MenuItem, Checkbox } from '@material-ui/core';
import { filterArray } from 'utils/functions';
import DmsService from 'Services/DMS/dms-api.service';
import useTranslation from 'Hooks/useTranslation';
import JobTableList from './JobTableList';
import debounce from "lodash.debounce";
import useJobSubscription from 'Components/SignalrSubscribes/JobSubscription.component';
import SlidePanel from 'Components/SlidePanel/SlidePanel';

const JOBS_PAGE_SIZE = 40;

const jobIdsMatch = (a, b) =>
    a != null && b != null && String(a) === String(b);

const ChangeJobModal = ({ jobs, title, isOpen, onCancel, onSave, onSelectionChanged, type, setCurrentJobUnits, currentJob, licensesNames }) => {
    const [value, setValue] = useState('');
    const [search, setSearch] = useState('');
    const [lastJobsCount, setLastJobsCount] = useState(0);
    const translation = useTranslation();
    const [anchorEl, setAnchorEl] = useState(null);
    const [pagedJobs, setPagedJobs] = useState([]);
    const [licenses, setLicenses] = useState(null);
    const [selectedLicenses, setSelectedLicenses] = useState([]);
    const listRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);
    const isLoadingRef = useRef(false);
    const lastFetchedPageLengthRef = useRef(0);
    const { subscribeToJob } = useJobSubscription(); 

    const handleChange = event => {
        setValue(event.target.value);
    };

    const allowedJobs = useMemo(() => {
        if (!Array.isArray(jobs)) return [];
        return jobs.filter((job) => !job.isDeleting);
    }, [jobs]);

    useEffect(() => {
        if (isOpen) {
            setPagedJobs([]);
            setLastJobsCount(0);
            lastFetchedPageLengthRef.current = 0;
        }
    }, [selectedLicenses, isOpen, search]);

    useEffect(() => {
        if (!isOpen) {
            isLoadingRef.current = false;
            setIsLoading(false);
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) {
            isLoadingRef.current = true;
            setIsLoading(true);
            const debouncedFetchJobs = debounce(() => {
                DmsService.getJobsShortPaged(0, JOBS_PAGE_SIZE, selectedLicenses, search)
                    .then((data) => {
                        const chunk = data || [];
                        lastFetchedPageLengthRef.current = chunk.length;
                        setPagedJobs(chunk);
                        isLoadingRef.current = false;
                        setIsLoading(false);
                    })
                    .catch((err) => {
                        console.error(err);
                        isLoadingRef.current = false;
                        setIsLoading(false);
                    });
            }, 300);

            debouncedFetchJobs();
            return () => {
                debouncedFetchJobs.cancel();
                isLoadingRef.current = false;
                setIsLoading(false);
            };
        }
    }, [isOpen, selectedLicenses, search]);

    useEffect(() => {
        if (isOpen) {
            DmsService.getLicenses()
                .then((data) => setLicenses(data))
                .catch((err) => console.error(err));
        }
    }, [isOpen]);

    const displayedJobs = useMemo(() => {
        if (!pagedJobs.length) return [];
        if (!allowedJobs.length) return pagedJobs;
        return pagedJobs.filter((job) =>
            allowedJobs.some((allowedJob) => jobIdsMatch(allowedJob.id, job.id))
        );
    }, [pagedJobs, allowedJobs]);

    const filteredJobs = useMemo(() => {
        if (search && search.trim() !== "") {
            return filterArray(allowedJobs, search);
        }
        return displayedJobs;
    }, [allowedJobs, displayedJobs, search]);

    useEffect(() => {
        if (!currentJob) return;
        setValue(String(currentJob.id));
    }, [currentJob]);

    useEffect(() => {
        if (!allowedJobs || allowedJobs.length <= lastJobsCount)
            return;
        const preferred =
            currentJob?.id != null && allowedJobs.some((j) => jobIdsMatch(j.id, currentJob.id))
                ? String(currentJob.id)
                : String(allowedJobs[0].id);
        setValue(preferred);
        setLastJobsCount(allowedJobs.length);
    }, [allowedJobs, lastJobsCount, currentJob]);

    useEffect(() => {
        if (filteredJobs.length) {
            if (!filteredJobs.find((job) => jobIdsMatch(job.id, value))) {
                const pick =
                    currentJob?.id != null && filteredJobs.some((j) => jobIdsMatch(j.id, currentJob.id))
                        ? String(currentJob.id)
                        : String(filteredJobs[0].id);
                setValue(pick);
            }
        } else if (isOpen) {
            if (search && search.trim() !== '') {
                setValue('');
            } else if (currentJob?.id != null) {
                setValue(String(currentJob.id));
            } else {
                setValue('');
            }
        }
    }, [filteredJobs, search, value, onSelectionChanged, isOpen, currentJob]);

    useEffect(() => {
        const fetchJobById = async (jobId) => {
            if (!jobId) return;
            const response = await DmsService.getJobsById(jobId);
            if (response) {
                setCurrentJobUnits(response.units);
                const job = allowedJobs.find(x => jobIdsMatch(x.id, jobId));
                if (job) {
                    job.units = response.units;
                }
            }
        };
        if (value && isOpen && type !== 'oneJobPassword') {
            fetchJobById(value).catch((err) => console.error(err));
        }
    }, [value, setCurrentJobUnits, allowedJobs, isOpen, type]);

    const handleSelectLicense = (license) => {
        setSelectedLicenses((prevSelected) => {
            if (prevSelected.includes(license.id)) {
                return prevSelected.filter((id) => id !== license.id);
            } else {
                return [...prevSelected, license.id];
            }
        });
    };

    const licensesData = useMemo(() => {
        if (!licenses || !licensesNames) return [];
        return licenses.map((licenseId) => {
            const licenseInfo = licensesNames.find(
                (ln) => ln.licenseId === licenseId
            );
            return {
                id: licenseId,
                license: licenseInfo ? licenseInfo.name : licenseId 
            };
        });
    }, [licenses, licensesNames]);

    const selectedLicensesLabel = useMemo(() => {
        if (selectedLicenses.length === 0 || !licensesData) return "";
        return licensesData
            .filter((l) => selectedLicenses.includes(l.id))
            .map((l) => l.license)
            .join(", ");
    }, [selectedLicenses, licensesData]);

    const popoverOpen = Boolean(anchorEl);
    const popoverId = popoverOpen ? "license-popover" : undefined;

    const handleOpenPopover = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClosePopover = () => {
        setAnchorEl(null);
    };

    const shouldLoadMorePagedJobs = useCallback(() => {
        if (isLoadingRef.current) return false;
        if (lastFetchedPageLengthRef.current < JOBS_PAGE_SIZE) return false;
        if (search && search.trim() !== "") return false;
        if (allowedJobs.length > 0 && displayedJobs.length >= allowedJobs.length) {
            return false;
        }
        return true;
    }, [search, allowedJobs.length, displayedJobs.length]);

    const loadMoreJobs = useCallback(() => {
        if (!shouldLoadMorePagedJobs()) return;
        isLoadingRef.current = true;
        setIsLoading(true);
        const skip = pagedJobs.length;
        DmsService.getJobsShortPaged(skip, JOBS_PAGE_SIZE, selectedLicenses, search)
            .then((data) => {
                const chunk = data || [];
                lastFetchedPageLengthRef.current = chunk.length;
                if (chunk.length > 0) {
                    setPagedJobs((prevJobs) => [...prevJobs, ...chunk]);
                }
            })
            .catch((err) => console.error(err))
            .finally(() => {
                isLoadingRef.current = false;
                setIsLoading(false);
            });
    }, [pagedJobs.length, selectedLicenses, search, shouldLoadMorePagedJobs]);

    const tryLoadMoreIfNoOverflow = useCallback(() => {
        if (!isOpen || pagedJobs.length === 0) return;
        if (!shouldLoadMorePagedJobs()) return;

        const el = listRef.current;
        if (!el) return;

        const threshold = 12;
        if (el.scrollHeight <= el.clientHeight + threshold) {
            loadMoreJobs();
        }
    }, [isOpen, loadMoreJobs, pagedJobs.length, shouldLoadMorePagedJobs]);

    useEffect(() => {
        const container = listRef.current;
        if (!container) return;

        const handleScroll = debounce(() => {
            const threshold = 100;
            if (
                container.scrollTop + container.clientHeight >=
                container.scrollHeight - threshold
            ) {
                loadMoreJobs();
            }
        }, 200);

        container.addEventListener("scroll", handleScroll);
        return () => {
            container.removeEventListener("scroll", handleScroll);
            handleScroll.cancel();
        };
    }, [loadMoreJobs]);

    useEffect(() => {
        if (!isOpen || isLoading) return;
        let cancelled = false;
        const id = requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (!cancelled) tryLoadMoreIfNoOverflow();
            });
        });
        return () => {
            cancelled = true;
            cancelAnimationFrame(id);
        };
    }, [isOpen, isLoading, pagedJobs.length, filteredJobs.length, tryLoadMoreIfNoOverflow]);

    useEffect(() => {
        if (!isOpen) return;
        const onResize = debounce(() => {
            tryLoadMoreIfNoOverflow();
        }, 150);
        window.addEventListener("resize", onResize);
        return () => {
            onResize.cancel();
            window.removeEventListener("resize", onResize);
        };
    }, [isOpen, tryLoadMoreIfNoOverflow]);

    const handleSubscribe = async () => {
        const selectedJob =
            jobs?.find((job) => jobIdsMatch(job.id, value)) ||
            filteredJobs.find((job) => jobIdsMatch(job.id, value));
        if (!selectedJob) {
            console.error("Selected job not found.");
            return;
        }

        await subscribeToJob(value, selectedJob.licenseId, () => onSave(value, false, selectedJob));
    };

    return (
        <SlidePanel
            open={isOpen}
            onClose={onCancel}
            title={title || "Select job"}
            className="appSlidePanel--wide"
            footer={
                <>
                    <Button onClick={onCancel} variant="outlined">
                        {translation.cancelButtonLabel}
                    </Button>
                    <Button
                        onClick={handleSubscribe}
                        disabled={!value}
                        variant="contained"
                        color="primary"
                    >
                        {translation.saveButtonLabel}
                    </Button>
                </>
            }
        >
            <div style={{ display: "flex", gap: "10px" }}>
                <TextField
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    name="search"
                    label={translation.dashboardSearchJobLabel}
                    type="text"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
                <TextField
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    name="license"
                    label="Filter by license"
                    value={selectedLicensesLabel}
                    onClick={handleOpenPopover}
                    InputProps={{
                        readOnly: true,
                    }}
                />
                <Popover
                    id={popoverId}
                    open={popoverOpen}
                    anchorEl={anchorEl}
                    onClose={handleClosePopover}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "left",
                    }}
                >
                    {licensesData?.map((license) => (
                        <MenuItem
                            key={license.id}
                            value={license.id}
                            onClick={() => handleSelectLicense(license)}
                            className="filterMenuItem"
                            style={{ width: "325px" }}
                        >
                            <Checkbox
                                size="small"
                                id={`checkbox-${license.id}`}
                                color="default"
                                checked={selectedLicenses.includes(license.id)}
                            />
                            {license.license}
                        </MenuItem>
                    ))}
                </Popover>
            </div>
            <JobTableList
                jobs={filteredJobs}
                value={value}
                onChange={handleChange}
                isRowDisabled={(job) => Boolean(job?.isDeleting)}
                isRowMuted={(job) => job?.isEnabled === false}
                licensesNames={licensesNames}
                listRef={listRef}
                isLoading={isLoading}
            />
        </SlidePanel>
    );
};

export default ChangeJobModal;